import { useEffect, useState } from "react";
import axios from "axios";
import config from "../../devprodConfig";
import { getSharePointFolderByKiinteisto } from "../../api/api";

const API_URL = config.apiBaseUrl;
const spCache = new Map();

export default function useSharePoint(card) {
  const [files, setFiles] = useState([]);
  const [loadingSP, setLoadingSP] = useState(true);
  const [spFolder, setSpFolder] = useState(null);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderStack, setFolderStack] = useState([]);
  const [isFallback, setIsFallback] = useState(false);
  const [currentFolderName, setCurrentFolderName] = useState(null);
  useEffect(() => {
    if (spFolder?.name && !currentFolderName) {
      setCurrentFolderName(spFolder.name);
    }
  }, [spFolder]);
  // 🔎 FETCH FOLDER ONLY (NO FILE FETCH HERE)
  useEffect(() => {
    if (!card) return;

    const key = `${card?.rakennukset_fulls?.[0]?.osoite}_${card?.rakennukset_fulls?.[0]?.postinumero}`;

    // ✅ LOAD FROM CACHE
    if (spCache.has(key)) {
      const cached = spCache.get(key);

      setSpFolder(cached.spFolder);
      setCurrentFolderId(prev => {
  // 🔒 never overwrite valid folder
  if (prev && prev !== null) return prev;

  return cached.currentFolderId ?? null;
});
      setFolderStack(cached.folderStack || []);
      setIsFallback(cached.isFallback || false);

      setFiles(cached.files || []);
      setLoadingSP(false);
      return;
    }

    const fetchFolder = async () => {
  try {
    // 🚫 STOP if no kiinteistoId
    if (!card?.id_kiinteisto) {
      console.log("⛔ Skipping SP fetch (no kiinteistoId)");
      return;
    }

    setLoadingSP(true);

    const res = await getSharePointFolderByKiinteisto(
      card?.rakennukset_fulls?.[0]?.osoite,
      card?.rakennukset_fulls?.[0]?.postinumero,
      card?.rakennukset_fulls?.[0]?.toimipaikka,
      card?.id_kiinteisto
    );

    const { folder, fallback } = res.data;

    setSpFolder(folder);
    setIsFallback(fallback);

    setCurrentFolderId(prev => {
      // 🔒 NEVER overwrite valid folder
      if (prev && prev !== null) return prev;

      return folder?.id ?? null;
    });

    // ✅ SEED CACHE — this is the only place a new entry is created;
    // the "CACHE NAVIGATION STATE" effect below only ever updates an
    // entry that already exists, so without this the cache read at the
    // top of this effect never hits and every mount refetches.
    spCache.set(key, { spFolder: folder, isFallback: fallback, currentFolderId: folder?.id ?? null, folderStack: [], files: [] });

  } catch (err) {
    console.error("SP fetch failed", err);
  } finally {
    setLoadingSP(false);
  }
};

    fetchFolder();
 }, [card?.id_kiinteisto]);

  // 📂 SINGLE SOURCE OF TRUTH FOR FILE FETCHING
  useEffect(() => {
 
  if (currentFolderId === null || currentFolderId === undefined) return;

  const fetchFiles = async () => {
    try {
      setLoadingSP(true);

      const res = await axios.get(`${API_URL}/sharepoint/files`, {
        params: { folderId: currentFolderId },
        withCredentials: true
      });

      setFiles(res.data);

      // ✅ MERGE INTO CACHE — the folder-fetch effect seeds the entry;
      // this keeps its files in sync as the user navigates folders.
      const key = `${card?.rakennukset_fulls?.[0]?.osoite}_${card?.rakennukset_fulls?.[0]?.postinumero}`;
      if (spCache.has(key)) {
        spCache.set(key, { ...spCache.get(key), files: res.data });
      }

    } catch (err) {
      console.error("SP files fetch failed", err);
    } finally {
      setLoadingSP(false);
    }
  };

  fetchFiles();
}, [currentFolderId]);

  // 💾 CACHE NAVIGATION STATE
  useEffect(() => {
    if (!card) return;

    const key = `${card?.rakennukset_fulls?.[0]?.osoite}_${card?.rakennukset_fulls?.[0]?.postinumero}`;

    if (!spCache.has(key)) return;

    const cached = spCache.get(key);

    spCache.set(key, {
      ...cached,
      currentFolderId,
      folderStack
    });

  }, [currentFolderId, folderStack]);

  return {
    files,
    loadingSP,
    spFolder,
    currentFolderId,
    setCurrentFolderId,
    folderStack,
    setFolderStack,
    isFallback
  };
}